import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { 
  AlertCircle, FileText, Loader2,
  Plane, BookmarkPlus
} from 'lucide-react';

// Sub-componentes extraidos
import { API_URL, aviationQuotes, loadingStages, MAX_CACHED_PAGES } from './pdf/pdfConstants';
import { throttledFetch, verifyImageIntegrity, checkJpegEOI } from './pdf/pdfUtils';
import ThumbnailImage from './pdf/ThumbnailImage';
import PDFLoadingScreen from './pdf/PDFLoadingScreen';
import PDFToolbar from './pdf/PDFToolbar';
import PDFFooter from './pdf/PDFFooter';
import PDFSearchPanel from './pdf/PDFSearchPanel';
import PDFPageList from './pdf/PDFPageList';
import PDFThumbnails from './pdf/PDFThumbnails';
import PDFTableOfContents from './pdf/PDFTableOfContents';
import PDFKeyboardHelp from './pdf/PDFKeyboardHelp';
import PDFPrintDialog from './pdf/PDFPrintDialog';
import PDFPageDisplay from './pdf/PDFPageDisplay';
import PDFContinuousView from './pdf/PDFContinuousView';
import PDFBookmarksPanel from './pdf/PDFBookmarksPanel';
import PDFBookmarkPopover from './pdf/PDFBookmarkPopover';

// Hooks aislados (no tocan el motor de render)
import useReadingProgress from '../hooks/useReadingProgress';
import useMarcadores from '../hooks/useMarcadores';
import useDocumentStats from '../hooks/useDocumentStats';

// URL del backend importada desde pdf/pdfConstants.js

// Constantes importadas desde pdf/pdfConstants.js y pdf/pdfUtils.js

// Constantes importadas desde pdf/pdfConstants.js y pdf/pdfUtils.js

const PDFViewerPro = ({ 
  isOpen, 
  onClose, 
  documentId,
  documentName = 'Documento',
  numeroReferencia = '',
  canPrint = false,
  isAdmin = false,
  isSuperAdmin = false,
  maxPrintPages = 25,
  userInfo = null
}) => {
  // Estado del documento
  const [docInfo, setDocInfo] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1.0);
  // Por defecto el visor abre en pantalla completa (mejora la inmersión de lectura)
  const [isFullscreen, setIsFullscreen] = useState(true);
  
  // Estado de animación de carga
  const [loadingStage, setLoadingStage] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentQuote, setCurrentQuote] = useState(0);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  
  // Estado de impresión
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printPages, setPrintPages] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [printError, setPrintError] = useState(null);
  const [printProgress, setPrintProgress] = useState(0);
  
  // Refs
  const containerRef = useRef(null);
  // Ref + state para medir dinámicamente la altura del toolbar — usado para anclar
  // los paneles laterales (Índice y Marcadores) EXACTAMENTE al ras del toolbar,
  // independientemente del DPI/zoom/cantidad de filas del navegador del usuario.
  const toolbarRef = useRef(null);
  const [toolbarHeight, setToolbarHeight] = useState(56);
  // FIX RACE CONDITION: Ref que se actualiza SINCRÓNICAMENTE cuando el usuario navega.
  // A diferencia de pageNumber (state, asíncrono), este ref refleja la página actual
  // INMEDIATAMENTE, cerrando la ventana de tiempo entre render y effect cleanup.
  const currentPageRef = useRef(1);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  // Dimensiones BASE (LOW quality) de la imagen - SIEMPRE usa dimensiones LOW para evitar que HD agrande la imagen
  const [imageDimensions, setImageDimensions] = useState({ width: 612, height: 792 }); // Default letter size
  // Ref para almacenar dimensiones LOW por página - persiste entre re-renders
  const pageBaseDimensionsRef = useRef({}); // { pageNum: { width, height } }
  
  // Estado para calidad adaptativa
  const [currentQuality, setCurrentQuality] = useState('low');
  const [isUpgradingQuality, setIsUpgradingQuality] = useState(false);
  // Tamaño en bytes de la imagen actual (para comparación en upgrade HD)
  const [currentImageSize, setCurrentImageSize] = useState(0);
  // FIX: Forzar re-render del navegador cuando la imagen cambia
  const [imageKey, setImageKey] = useState(0);

  // Estado de búsqueda
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  
  // Estado para nuevas funcionalidades
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [nightMode, setNightMode] = useState(false);
  // Mostrar índice por defecto SOLO en escritorio (en mobile ocupa toda la pantalla)
  const [showTableOfContents, setShowTableOfContents] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= 1024;
  });
  const [tableOfContents, setTableOfContents] = useState([]);
  const [tocLoading, setTocLoading] = useState(false);
  const [isIndexed, setIsIndexed] = useState(false);
  const [indexingInProgress, setIndexingInProgress] = useState(false);

  // Rotación de página
  const [rotation, setRotation] = useState(0);
  
  // Thumbnails
  const [showThumbnails, setShowThumbnails] = useState(false);
  
  // Modo lectura continua (por defecto activado)
  const [continuousMode, setContinuousMode] = useState(true);
  const previousScaleRef = useRef(1.0);
  
  // Detectar mobile y ajustar scale al cambiar modo continuo
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (continuousMode && isMobile) {
      previousScaleRef.current = scale;
      setScale(0.5);
    } else if (!continuousMode && previousScaleRef.current !== scale) {
      // Restaurar scale anterior al volver a modo pagina
      setScale(previousScaleRef.current);
    }
  }, [continuousMode]); // Solo reaccionar al toggle, no al scale
  
  // Ref para cancelar reintentos de TOC cuando cambia de documento
  const tocRetryTimeoutRef = useRef(null);
  const currentDocumentIdRef = useRef(null);
  
  // Cache de páginas prefetched (para mejorar navegación)
  const prefetchCache = useRef(new Map());
  // Cache de páginas ya visitadas (carga instantánea al volver)
  const visitedCache = useRef(new Map());
  // Orden de páginas visitadas (para LRU - Least Recently Used)
  const visitedOrder = useRef([]);
  // Estado para indicar qué páginas están en cache (para UI)
  const [cachedPages, setCachedPages] = useState(new Set());
  // Ref para cancelar solicitudes en progreso cuando el usuario navega rápido
  const currentLoadControllerRef = useRef(null);
  // Ref para trackear currentImageUrl sin causar re-ejecución de efectos
  const currentImageUrlRef = useRef(null);

  // Función para agregar página al cache con límite LRU
  // Almacena URL + dimensiones para restaurar correctamente al navegar entre páginas
  const addToVisitedCache = (cacheKey, url, pageNum, width, height) => {
    // Si ya existe, mover al final (más reciente)
    const existingIndex = visitedOrder.current.indexOf(cacheKey);
    if (existingIndex !== -1) {
      visitedOrder.current.splice(existingIndex, 1);
    }
    visitedOrder.current.push(cacheKey);
    visitedCache.current.set(cacheKey, { url, width: width || 0, height: height || 0 });
    
    // Si excede el límite, eliminar del cache (pero NO revocar el blob)
    while (visitedOrder.current.length > MAX_CACHED_PAGES) {
      const oldestKey = visitedOrder.current.shift();
      visitedCache.current.delete(oldestKey);
    }
    
    // Actualizar indicador visual
    setCachedPages(prev => {
      const newSet = new Set([...prev, pageNum]);
      if (newSet.size > MAX_CACHED_PAGES) {
        const arr = [...newSet];
        return new Set(arr.slice(-MAX_CACHED_PAGES));
      }
      return newSet;
    });
  };

  // Animación de etapas de carga
  useEffect(() => {
    if (!loading) return;
    
    const stageInterval = setInterval(() => {
      setLoadingStage(prev => (prev + 1) % loadingStages.length);
    }, 2500);
    
    const quoteInterval = setInterval(() => {
      setCurrentQuote(prev => (prev + 1) % aviationQuotes.length);
    }, 5000);
    
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 95) return prev;
        return prev + Math.random() * 15;
      });
    }, 500);
    
    return () => {
      clearInterval(stageInterval);
      clearInterval(quoteInterval);
      clearInterval(progressInterval);
    };
  }, [loading]);

  // Mantener ref sincronizado con state (para efectos que no deben re-dispararse por cambio de URL)
  useEffect(() => {
    currentImageUrlRef.current = currentImageUrl;
  }, [currentImageUrl]);

  // Cargar info del documento al abrir
  useEffect(() => {
    if (isOpen && documentId) {
      // IMPORTANTE: Actualizar referencia del documento actual
      currentDocumentIdRef.current = documentId;

      // Restablecer preferencias por defecto cada vez que se abre el visor
      // (Pantalla completa + Lectura continua + Índice abierto en escritorio)
      setIsFullscreen(true);
      setContinuousMode(true);
      setShowTableOfContents(typeof window !== 'undefined' && window.innerWidth >= 1024);

      // CANCELAR reintentos de TOC del documento anterior
      if (tocRetryTimeoutRef.current) {
        clearTimeout(tocRetryTimeoutRef.current);
        tocRetryTimeoutRef.current = null;
      }
      
      // IMPORTANTE: Resetear estado del documento anterior ANTES de cargar el nuevo
      setTableOfContents([]);  // Limpiar TOC del documento anterior
      setTocLoading(true);     // Mostrar loading mientras carga
      setCurrentImageUrl(null); // Limpiar imagen del documento anterior
      setCurrentImageSize(0);   // FIX: Resetear tamaño de imagen
      setCurrentQuality('low'); // FIX: Resetear calidad

      // Resetear página a 1 (estado limpio para el nuevo documento)
      currentPageRef.current = 1;
      setPageNumber(1);

      setError(null);          // Limpiar errores anteriores
      pageBaseDimensionsRef.current = {}; // Limpiar dimensiones base del doc anterior
      
      loadDocumentInfo();
      // Resetear caches para el nuevo documento
      setCachedPages(new Set());
      visitedOrder.current = [];
    }
    return () => {
      // Limpiar al desmontar o cambiar de documento
      if (tocRetryTimeoutRef.current) {
        clearTimeout(tocRetryTimeoutRef.current);
        tocRetryTimeoutRef.current = null;
      }
      // Limpiar caches al cerrar el visor
      prefetchCache.current.clear();
      visitedCache.current.clear();
      visitedOrder.current = [];
      setCachedPages(new Set());
      setTableOfContents([]);  // Limpiar TOC al cerrar
      isInitialLoadRef.current = true; // Reset para el proximo documento que se abra
      setDocInfo(null);        // Limpiar info al CERRAR (no al cambiar)
    };
  }, [isOpen, documentId]);

  // Ref para el debounce de navegación rápida
  const navigationDebounceRef = useRef(null);
  const lastRequestedPageRef = useRef(null);
  // FIRST-LOAD FLAG: primera carga del documento usa debounce corto (200ms) para que
  // pagina 1 se decodifique ANTES de que el render del TOC bloquee el main thread.
  // Cargas siguientes usan 700ms para filtrar scroll furioso (TOC, miniaturas, scrollbar).
  // Se resetea cuando se abre un documento nuevo (cleanup de [isOpen, documentId]).
  const isInitialLoadRef = useRef(true);

  // Cargar página cuando cambia el número - CON DEBOUNCE para navegación rápida
  useEffect(() => {
    if (!docInfo || pageNumber < 1 || pageNumber > docInfo.total_pages) return;
    
    // Guardar la página solicitada
    lastRequestedPageRef.current = pageNumber;
    
    // Limpiar timeout anterior
    if (navigationDebounceRef.current) {
      clearTimeout(navigationDebounceRef.current);
    }
    
    // DEBOUNCE DINAMICO:
    // - Primera carga del documento: 200ms (pagina 1 se decodifica antes de que el TOC bloquee el thread)
    // - Cargas posteriores: 700ms (filtra el scroll furioso por TOC/miniaturas/scrollbar)
    const debounceMs = isInitialLoadRef.current ? 200 : 700;

    navigationDebounceRef.current = setTimeout(() => {
      // Verificar que sigue siendo la página que queremos
      if (lastRequestedPageRef.current === pageNumber) {
        // Cancelar cualquier solicitud anterior en curso
        if (currentLoadControllerRef.current) {
          currentLoadControllerRef.current.abort();
          currentLoadControllerRef.current = null;
        }
        loadPage(pageNumber);
        isInitialLoadRef.current = false; // Salio la primera carga; navegacion siguiente usa 700ms
      }
    }, debounceMs);
    
    return () => {
      if (navigationDebounceRef.current) {
        clearTimeout(navigationDebounceRef.current);
      }
    };
    // FIX: Removido 'scale' de las dependencias para evitar que un cambio de zoom
    // recargue la página a baja calidad. El efecto de quality upgrade se encarga
    // de mejorar la calidad cuando el zoom lo requiere.
  }, [pageNumber, docInfo]);

  // Estado para tracking de documento en preparación
  const [documentPreparing, setDocumentPreparing] = useState(false);

  // SAFETY NET: Solo actuar si NO estamos esperando preparación de documento
  // Para documentos grandes (>50MB), el servidor puede tardar minutos en preparar
  //
  // NOTA IMPORTANTE: NO hacer auto-retry aqui. La funcion `loadPage` ya tiene su propio
  // mecanismo de retry interno (hasta 11 reintentos con backoff exponencial 2s-22s) que
  // se activa al recibir AbortError. Si esta safety net hace abort+reload, entra en
  // conflicto con ese retry interno y se generan loops paralelos sobre la misma pagina.
  // Solo logueamos para diagnostico; el usuario puede usar el boton de recargar si lo necesita.
  useEffect(() => {
    if (!pageLoading || !docInfo || documentPreparing) return;

    const safetyTimeout = setTimeout(() => {
      if (pageLoading && lastRequestedPageRef.current === pageNumber && !documentPreparing) {
        console.log(`[PDF] Safety net: página ${pageNumber} lleva 60s cargando sin preparación activa`);
        // NO cancelar automaticamente - el retry interno de loadPage ya maneja esto.
      }
    }, 60000);

    return () => clearTimeout(safetyTimeout);
  }, [pageLoading, pageNumber, docInfo, documentPreparing]);

  // PREFETCH EXTENDIDO: Precargar paginas vecinas en background
  // FIX 2026-04: Cancelar prefetches en vuelo al navegar y saltear durante scroll furioso
  const prefetchControllersRef = useRef([]); // AbortControllers de prefetches activos
  const lastPageChangeAtRef = useRef(0);     // timestamp del ultimo cambio de pagina

  useEffect(() => {
    // Marcar momento de cambio de pagina (para detectar scroll furioso)
    const now = Date.now();
    const timeSinceLastChange = now - lastPageChangeAtRef.current;
    lastPageChangeAtRef.current = now;

    // Cancelar todos los prefetches anteriores en vuelo
    if (prefetchControllersRef.current.length > 0) {
      prefetchControllersRef.current.forEach(c => {
        try { c.abort(); } catch (_) {}
      });
      prefetchControllersRef.current = [];
    }

    if (!docInfo || !currentImageUrlRef.current || pageLoading) return;

    // SCROLL FURIOSO: Si el usuario cambio de pagina hace menos de 1500ms,
    // NO disparar prefetch - es probable que siga navegando rapido
    const RAPID_NAV_THRESHOLD_MS = 1500;
    const isRapidNav = timeSinceLastChange < RAPID_NAV_THRESHOLD_MS && lastPageChangeAtRef.current > 0;

    // Prefetch de paginas vecinas SIEMPRE en low para no saturar memoria movil
    const quality = 'low';

    const prefetchPage = async (pageNum) => {
      if (pageNum < 1 || pageNum > docInfo.total_pages) return false;

      const cacheKey = `${documentId}_${pageNum}_${quality}`;
      // Ya esta en prefetch o visitado cache
      if (prefetchCache.current.has(cacheKey) || visitedCache.current.has(cacheKey)) return true;

      // Crear AbortController dedicado para este prefetch
      const controller = new AbortController();
      prefetchControllersRef.current.push(controller);

      try {
        const token = localStorage.getItem('token');

        const response = await throttledFetch(
          `${API_URL}/api/documentos/manuales/${documentId}/visor/pagina/${pageNum}?quality=${quality}`,
          { headers: { 'Authorization': `Bearer ${token}` }, signal: controller.signal }
        );

        if (response.ok) {
          const blob = await response.blob();
          if (blob.type.startsWith('image/')) {
            // Verificacion COMPLETA antes de cachear: tamano + JPEG EOI
            const expectedSize = parseInt(response.headers.get('X-Image-Size') || '0');
            const integrity = await verifyImageIntegrity(blob, expectedSize);
            if (!integrity.isComplete) {
              console.warn(`[Prefetch] JPEG TRUNCADO/CORRUPTO pag ${pageNum}: ${integrity.reason || 'size_mismatch'} (${blob.size} bytes) - NO cacheado`);
              return false;
            }
            const blobUrl = URL.createObjectURL(blob);
            // Obtener dimensiones del header si esta disponible
            const imgW = parseInt(response.headers.get('X-Image-Width') || '0');
            const imgH = parseInt(response.headers.get('X-Image-Height') || '0');
            prefetchCache.current.set(cacheKey, { url: blobUrl, width: imgW, height: imgH });
            setCachedPages(prev => new Set([...prev, pageNum]));
            return true;
          }
        }
      } catch (e) {
        // Silenciosamente ignorar errores de prefetch (incluyendo AbortError)
      }
      return false;
    };

    // Delay aumentado para no competir con la carga principal
    // Si el usuario sigue navegando, este timer se cancela en el cleanup
    const prefetchDelay = isRapidNav ? 0 : 1500; // 1500ms en navegacion normal

    const timer = setTimeout(async () => {
      // Si fue scroll furioso, salteamos el prefetch completo
      if (isRapidNav) {
        return;
      }
      // Precargar 3 paginas adelante (prioridad)
      await prefetchPage(pageNumber + 1);
      await prefetchPage(pageNumber + 2);
      await prefetchPage(pageNumber + 3);
      // Precargar 1 pagina atras
      await prefetchPage(pageNumber - 1);
    }, prefetchDelay);

    return () => {
      clearTimeout(timer);
      // Cancelar prefetches en vuelo al desmontar/cambiar pagina
      prefetchControllersRef.current.forEach(c => {
        try { c.abort(); } catch (_) {}
      });
      prefetchControllersRef.current = [];
    };
  }, [pageNumber, docInfo, scale, pageLoading, documentId]);

  // CALIDAD ADAPTATIVA: Mejorar imagen cuando el usuario hace zoom manual
  // FIX 2026-02: Imágenes HD se truncaban por ser PNG gigantes (2-5MB)
  // SOLUCIÓN: Backend ahora envía JPEG (5-10x más chico) + header X-Image-Size para verificar integridad
  // Si la imagen llega truncada, se reintenta hasta 5 veces con backoff
  
  // Ref para controlar los reintentos en background
  const hdRetryIntervalRef = useRef(null);
  const hdRetryPageRef = useRef(null);
  // Map de contadores de reintento por página: { "docId_pageNum": count }
  // Persiste entre re-renders del efecto, se resetea solo al cambiar documento
  const hdRetryCountsRef = useRef({});
  // Ref para evitar re-entrada (no usar state para no causar re-render del efecto)
  const isUpgradingRef = useRef(false);
  // FIX RACE CONDITION: Contador de generación para invalidar upgrades obsoletos.
  // Se incrementa cada vez que el efecto de HD se ejecuta. Si el usuario navega
  // rápido (ej: pág 101 → 102 → 101), la primera y segunda visita a 101 tendrán
  // generaciones distintas, evitando que el upgrade viejo contamine el estado actual.
  const upgradeGenerationRef = useRef(0);
  // AbortController para cancelar fetches HD en vuelo al navegar
  const hdFetchControllerRef = useRef(null);
  
  // Resetear contadores cuando cambia el documento
  useEffect(() => {
    hdRetryCountsRef.current = {};
  }, [documentId]);
  
  useEffect(() => {
    // Limpiar intervalo anterior
    if (hdRetryIntervalRef.current) {
      clearInterval(hdRetryIntervalRef.current);
      hdRetryIntervalRef.current = null;
    }
    
    // Condiciones para NO intentar upgrade
    if (!currentImageUrlRef.current || pageLoading) return;
    if (currentQuality === 'high') return;
    // SIEMPRE intentar upgrade a HD para la página activa.
    // El zoom nativo del navegador no actualiza el state 'scale' de React,
    // así que cargamos HD por defecto para que al hacer pinch-to-zoom el texto sea nítido.
    if (isUpgradingRef.current) return;
    
    const MAX_HD_RETRIES = 5;
    const RETRY_INTERVAL = 5000;
    const targetQuality = 'high';
    const retryKey = `${documentId}_${pageNumber}`;
    
    // Verificar si ya agotamos reintentos para esta página
    if ((hdRetryCountsRef.current[retryKey] || 0) >= MAX_HD_RETRIES) {
      console.log(`[Quality upgrade] Página ${pageNumber}: máximo de reintentos alcanzado, manteniendo low-res`);
      return;
    }
    
    // Guardar la página actual para verificar si cambió
    hdRetryPageRef.current = pageNumber;
    
    // FIX RACE CONDITION: Generar ID único para esta invocación del efecto.
    // Cada navegación incrementa el contador, invalidando upgrades previos.
    const myGeneration = ++upgradeGenerationRef.current;
    
    // Cancelar fetch HD anterior si está en vuelo
    if (hdFetchControllerRef.current) {
      hdFetchControllerRef.current.abort();
      hdFetchControllerRef.current = null;
    }
    
    const attemptHDUpgrade = async (isBackgroundRetry = false) => {
      // GUARD 1 (PRIMARIO): currentPageRef se actualiza SINCRÓNICAMENTE en goToPage.
      // Si el usuario ya navegó a otra página, descartar inmediatamente.
      if (currentPageRef.current !== pageNumber) {
        return { success: false, shouldRetry: false };
      }
      // GUARD 2: Verificar generación (previene race condition 101→102→101)
      if (upgradeGenerationRef.current !== myGeneration) {
        return { success: false, shouldRetry: false };
      }
      // GUARD 3: Verificar que hdRetryPageRef coincide
      if (hdRetryPageRef.current !== pageNumber) {
        return { success: false, shouldRetry: false };
      }
      
      const cacheKey = `${documentId}_${pageNumber}_${targetQuality}`;
      
      // Si ya está en cache visitado, usarla directamente
      if (visitedCache.current.has(cacheKey)) {
        const cached = visitedCache.current.get(cacheKey);
        const cachedUrl = cached.url || cached;
        if (cachedUrl !== currentImageUrlRef.current) {
          setCurrentImageUrl(cachedUrl);
          setCurrentQuality(targetQuality);
          // NO incrementar imageKey - solo actualizar src
        }
        return { success: true, shouldRetry: false };
      }
      
      if (!isBackgroundRetry) {
        isUpgradingRef.current = true;
        setIsUpgradingQuality(true);
      }
      
      try {
        const token = localStorage.getItem('token');
        const controller = new AbortController();
        hdFetchControllerRef.current = controller;
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        
        const currentRetries = hdRetryCountsRef.current[retryKey] || 0;
        const hdCacheBust = currentRetries >= 2 ? `&cb=${Date.now()}` : '';
        const response = await throttledFetch(
          `${API_URL}/api/documentos/manuales/${documentId}/visor/pagina/${pageNumber}?quality=${targetQuality}${hdCacheBust}`,
          { 
            headers: { 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }
        );
        
        clearTimeout(timeoutId);
        
        if (response.status === 202) {
          console.log('[Quality upgrade] Backend preparando página HD...');
          return { success: false, shouldRetry: true };
        }
        
        if (!response.ok) {
          console.log(`[Quality upgrade] Error ${response.status}`);
          return { success: false, shouldRetry: true };
        }
        
        // ============================================
        // INTEGRITY CHECK - Verificar que la imagen llegó completa
        // ============================================
        const expectedSize = parseInt(response.headers.get('X-Image-Size') || '0');
        const blob = await response.blob();
        
        if (!blob.type.startsWith('image/')) {
          console.warn('[Quality upgrade] Respuesta no es imagen');
          return { success: false, shouldRetry: false };
        }
        
        const hdSize = blob.size;
        
        // Verificación de integridad: comparar tamaño + verificar JPEG EOI
        const integrity = await verifyImageIntegrity(blob, expectedSize);
        if (!integrity.isComplete) {
          console.warn(`[Quality upgrade] IMAGEN TRUNCADA: ${hdSize} de ${expectedSize} bytes (${integrity.receivedPercent}%)`);
          return { success: false, shouldRetry: true };
        }
        
        // Verificación de tamaño mínimo
        if (hdSize < 5000) {
          console.warn(`[Quality upgrade] HD muy pequeña (${hdSize} bytes)`);
          return { success: false, shouldRetry: true };
        }
        // ============================================
        
        const newUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.src = newUrl;
        
        try {
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Timeout')), 15000);
            img.onload = async () => {
              clearTimeout(timeout);
              try { await img.decode(); resolve(); }
              catch (e) { reject(new Error('Decode failed')); }
            };
            img.onerror = () => { clearTimeout(timeout); reject(new Error('Load error')); };
          });
          
          if (img.naturalWidth < 100 || img.naturalHeight < 100) {
            URL.revokeObjectURL(newUrl);
            return { success: false, shouldRetry: true };
          }
          
          // GUARD CRÍTICO antes de aplicar estado HD:
          // currentPageRef se actualiza SINCRÓNICAMENTE, cerrando la ventana de tiempo
          // entre render y effect cleanup donde el viejo callback HD puede colarse.
          if (currentPageRef.current !== pageNumber || upgradeGenerationRef.current !== myGeneration) {
            URL.revokeObjectURL(newUrl);
            console.log(`[Quality upgrade] HD descartado: página actual=${currentPageRef.current}, HD era para=${pageNumber}, gen mía=${myGeneration}, gen actual=${upgradeGenerationRef.current}`);
            return { success: false, shouldRetry: false };
          }
          
          // ÉXITO - aplicar imagen HD
          const retryCount = hdRetryCountsRef.current[retryKey] || 0;
          console.log(`[Quality upgrade] HD OK: ${img.naturalWidth}x${img.naturalHeight}, ${hdSize} bytes${retryCount > 0 ? ` (intento ${retryCount + 1})` : ''}`);
          
          setCurrentImageUrl(newUrl);
          setCurrentImageSize(hdSize);
          // NO actualizar imageDimensions: mantener las del LOW para no causar saltos de tamaño
          // La imagen HD se muestra al mismo tamaño visual con mejor calidad
          addToVisitedCache(cacheKey, newUrl, pageNumber, img.naturalWidth, img.naturalHeight);
          setCurrentQuality(targetQuality);
          // NO incrementar imageKey - dejar que React actualice el src sin remontar el img
          // El remount causaba flashes y race conditions con las dimensiones
          
          return { success: true, shouldRetry: false };
          
        } catch (verifyError) {
          console.warn(`[Quality upgrade] ${verifyError.message}`);
          URL.revokeObjectURL(newUrl);
          return { success: false, shouldRetry: true };
        }
        
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.log(`[Quality upgrade] Error de red: ${e.message}`);
        }
        return { success: false, shouldRetry: e.name !== 'AbortError' };
      } finally {
        if (!isBackgroundRetry) {
          isUpgradingRef.current = false;
          setIsUpgradingQuality(false);
        }
      }
    };
    
    // Función principal
    const startUpgradeProcess = async () => {
      const result = await attemptHDUpgrade(false);
      
      if (result.success || !result.shouldRetry) return;
      
      // Iniciar reintentos en background
      const currentRetries = hdRetryCountsRef.current[retryKey] || 0;
      if (currentRetries >= MAX_HD_RETRIES) return;
      
      console.log(`[Quality upgrade] Iniciando reintentos background (${MAX_HD_RETRIES - currentRetries} restantes)...`);
      
      hdRetryIntervalRef.current = setInterval(async () => {
        // Incrementar contador PERSISTENTE (no se resetea entre re-renders)
        hdRetryCountsRef.current[retryKey] = (hdRetryCountsRef.current[retryKey] || 0) + 1;
        const retries = hdRetryCountsRef.current[retryKey];
        
        if (retries > MAX_HD_RETRIES) {
          console.warn(`[Quality upgrade] Máximo de ${MAX_HD_RETRIES} reintentos para página ${pageNumber}. Manteniendo low-res.`);
          clearInterval(hdRetryIntervalRef.current);
          hdRetryIntervalRef.current = null;
          return;
        }
        
        if (currentPageRef.current !== pageNumber || upgradeGenerationRef.current !== myGeneration) {
          clearInterval(hdRetryIntervalRef.current);
          hdRetryIntervalRef.current = null;
          return;
        }
        
        console.log(`[Quality upgrade] Reintento ${retries}/${MAX_HD_RETRIES} para página ${pageNumber}...`);
        const retryResult = await attemptHDUpgrade(true);
        
        if (retryResult.success || !retryResult.shouldRetry) {
          clearInterval(hdRetryIntervalRef.current);
          hdRetryIntervalRef.current = null;
        }
      }, RETRY_INTERVAL);
    };
    
    const timer = setTimeout(startUpgradeProcess, 2000);
    
    return () => {
      clearTimeout(timer);
      if (hdRetryIntervalRef.current) {
        clearInterval(hdRetryIntervalRef.current);
        hdRetryIntervalRef.current = null;
      }
      // Cancelar fetch HD en vuelo
      if (hdFetchControllerRef.current) {
        hdFetchControllerRef.current.abort();
        hdFetchControllerRef.current = null;
      }
    };
    // NOTA: Solo dependemos de lo necesario. NO incluir currentImageUrl, isUpgradingQuality, currentImageSize, imageDimensions
    // para evitar bucles infinitos. Usamos currentImageUrlRef para verificar sin causar re-ejecución.
  }, [pageLoading, currentQuality, documentId, pageNumber]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || !docInfo) return;
    
    const handleKeyDown = (e) => {
      // Ignorar si estamos escribiendo en un input
      if (e.target.tagName === 'INPUT') return;
      
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goToPage(pageNumber - 1);
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        goToPage(pageNumber + 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        goToPage(1);
      } else if (e.key === 'End') {
        e.preventDefault();
        goToPage(docInfo.total_pages);
      } else if (e.key === 'Escape') {
        if (showKeyboardHelp) {
          setShowKeyboardHelp(false);
        } else if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        setIsFullscreen(!isFullscreen);
      } else if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        setNightMode(!nightMode);
      } else if (e.key === '?' || e.key === 'h' || e.key === 'H') {
        e.preventDefault();
        setShowKeyboardHelp(!showKeyboardHelp);
      } else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        handleRotate();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, docInfo, pageNumber, isFullscreen, showKeyboardHelp, nightMode, rotation]);

  const loadDocumentInfo = async () => {
    setLoading(true);
    setError(null);
    setDocInfo(null);
    currentPageRef.current = 1; // SYNC: resetear ref
    setPageNumber(1);
    setLoadingProgress(0);
    setLoadingStage(0);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/documentos/manuales/${documentId}/visor/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error cargando documento');
      }
      
      const info = await response.json();
      setDocInfo(info);
      setLoadingProgress(100);
      
      // Pequeña pausa para mostrar el 100% antes de ocultar
      setTimeout(() => setLoading(false), 500);
      
      // Cargar TOC en background (no bloquea) - pasar info para que generatePageBasedToc funcione
      loadTableOfContents(info);
      
    } catch (err) {
      console.error('Error loading document info:', err);
      setError(err.message || 'No se pudo cargar el documento');
      setLoading(false);
    }
  };

  // Cargar Tabla de Contenidos (índice)
  const loadTableOfContents = async (currentDocInfo = null) => {
    // IMPORTANTE: Capturar el documentId al momento de llamar esta función
    const expectedDocumentId = documentId;
    
    setTocLoading(true);
    const docInfoToUse = currentDocInfo || docInfo;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/documentos/manuales/${documentId}/visor/toc`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // VERIFICAR que el documento no haya cambiado durante el fetch
      if (currentDocumentIdRef.current !== expectedDocumentId) {
        console.log('[PDF] TOC descartado - documento cambió durante la carga');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        
        // VERIFICAR de nuevo después de parsear JSON
        if (currentDocumentIdRef.current !== expectedDocumentId) {
          console.log('[PDF] TOC descartado - documento cambió');
          return;
        }
        
        if (data.toc && data.toc.length > 0) {
          // TOC real del PDF - reemplaza cualquier índice anterior
          setTableOfContents(data.toc);
          console.log(`[PDF] TOC cargado: ${data.toc.length} items para doc ${expectedDocumentId.substring(0,8)}`);
          setTocLoading(false);
        } else if (data.pending_extraction) {
          // El PDF aún no está en cache, esperar sin generar fallback
          console.log('[PDF] TOC pendiente de extracción, reintentando en 5s...');
          setTocLoading(true);
          
          // Cancelar timeout anterior si existe
          if (tocRetryTimeoutRef.current) {
            clearTimeout(tocRetryTimeoutRef.current);
          }
          
          // Guardar timeout para poder cancelarlo si cambia el documento
          tocRetryTimeoutRef.current = setTimeout(() => {
            // VERIFICAR que seguimos en el mismo documento
            if (currentDocumentIdRef.current === expectedDocumentId) {
              loadTableOfContents(docInfoToUse);
            }
          }, 5000);
        } else {
          // Confirmado que no hay TOC del PDF, ahora sí generar índice por páginas
          console.log('[PDF] PDF sin bookmarks, generando navegación por páginas');
          generatePageBasedToc(docInfoToUse);
          setTocLoading(false);
        }
      } else {
        // Si falla el request, generar índice por páginas
        generatePageBasedToc(docInfoToUse);
        setTocLoading(false);
      }
    } catch (err) {
      console.error('Error loading TOC:', err);
      // Fallback: generar índice por páginas
      if (currentDocumentIdRef.current === expectedDocumentId) {
        generatePageBasedToc(docInfoToUse);
        setTocLoading(false);
      }
    }
  };

  // Generar índice basado en páginas cuando no hay TOC del PDF
  const generatePageBasedToc = (currentDocInfo = null) => {
    const docInfoToUse = currentDocInfo || docInfo;
    if (!docInfoToUse || !docInfoToUse.total_pages) {
      console.log('[PDF] No se puede generar navegación por páginas: falta total_pages');
      return;
    }
    
    const totalPages = docInfoToUse.total_pages;
    const generatedToc = [];
    
    // Determinar el intervalo según cantidad de páginas
    let interval;
    if (totalPages <= 20) {
      interval = 5; // Cada 5 páginas para docs pequeños
    } else if (totalPages <= 100) {
      interval = 10; // Cada 10 páginas
    } else if (totalPages <= 500) {
      interval = 25; // Cada 25 páginas
    } else {
      interval = 50; // Cada 50 páginas para docs muy grandes
    }
    
    for (let i = 1; i <= totalPages; i += interval) {
      const endPage = Math.min(i + interval - 1, totalPages);
      generatedToc.push({
        level: 1,
        title: i === endPage ? `Página ${i}` : `Páginas ${i} - ${endPage}`,
        page_num: i,
        isGenerated: true // Marcar como generado
      });
    }
    
    setTableOfContents(generatedToc);
    console.log(`[PDF] Índice por páginas generado: ${generatedToc.length} items`);
  };

  // Iniciar indexación en background
  const startBackgroundIndexing = async () => {
    if (indexingInProgress) return;
    
    setIndexingInProgress(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/documentos/manuales/${documentId}/visor/indexar-background`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.already_indexed) {
          setIsIndexed(true);
        } else if (data.started) {
          console.log('[PDF] Indexación iniciada en background');
          // Verificar después de un tiempo si se completó
          setTimeout(async () => {
            // Recargar TOC por si se generó
            loadTableOfContents();
            setIndexingInProgress(false);
          }, 10000);
        }
      }
    } catch (err) {
      console.error('Error starting indexing:', err);
    } finally {
      setIndexingInProgress(false);
    }
  };

  const loadPage = async (pageNum, retryCount = 0) => {
    // CALIDAD ADAPTATIVA: Empezar con calidad baja para carga rápida
    // La calidad se mejorará automáticamente después si el zoom lo requiere
    const initialQuality = 'low';
    
    // FIX CRÍTICO: Si no tenemos dimensiones base (LOW) para esta página,
    // SIEMPRE empezar con LOW para capturar las dimensiones correctas.
    // Sin esto, la imagen se mostraría con dimensiones por defecto (612x792) 
    // que son MÁS GRANDES que las reales, causando que la página se vea "cortada".
    const hasBaseDims = !!pageBaseDimensionsRef.current[pageNum];
    const targetQuality = hasBaseDims 
      ? (scale >= 1.5 ? 'high' : scale >= 1.0 ? 'medium' : 'low')
      : 'low'; // Sin dims base → forzar LOW para capturarlas
    
    // Buscar en cache con la calidad objetivo
    const targetCacheKey = `${documentId}_${pageNum}_${targetQuality}`;
    
    // OPTIMIZACIÓN 1: Usar página de cache de visitadas (instantáneo)
    if (visitedCache.current.has(targetCacheKey)) {
      const cached = visitedCache.current.get(targetCacheKey);
      setCurrentImageUrl(cached.url);
      // SIEMPRE usar dimensiones BASE (LOW) — nunca las del HD que son más grandes
      const baseDims = pageBaseDimensionsRef.current[pageNum];
      if (baseDims) {
        setImageDimensions({ width: baseDims.width, height: baseDims.height });
      } else if (cached.width > 0 && cached.height > 0) {
        // No tenemos base dims — aproximar desde la calidad actual
        let w = cached.width, h = cached.height;
        if (targetQuality === 'medium' || targetQuality === 'high') {
          // Medium/high son ~2x LOW en DPI → dividir entre 2
          w = Math.round(w / 2);
          h = Math.round(h / 2);
        }
        setImageDimensions({ width: w, height: h });
        pageBaseDimensionsRef.current[pageNum] = { width: w, height: h };
        console.log(`[PDF] Dims base aproximadas desde ${targetQuality}: ${w}x${h}`);
      }
      setCurrentQuality(targetQuality);
      setPageLoading(false);
      return;
    }
    
    // OPTIMIZACIÓN 2: Usar página prefetched si está disponible (CON VERIFICACIÓN DE INTEGRIDAD)
    if (prefetchCache.current.has(targetCacheKey)) {
      const prefetched = prefetchCache.current.get(targetCacheKey);
      const prefetchedUrl = typeof prefetched === 'string' ? prefetched : prefetched.url;
      
      // DEFENSA EN PROFUNDIDAD: Re-verificar JPEG antes de mostrar
      // Aunque prefetchPage ya verifica, esta segunda capa previene mostrar JPEGs
      // truncados que pudieron colarse por race conditions o errores de memoria
      let prefetchValid = true;
      try {
        const verifyResp = await fetch(prefetchedUrl);
        const verifyBlob = await verifyResp.blob();
        const jpegOk = await checkJpegEOI(verifyBlob);
        if (!jpegOk) {
          console.warn(`[loadPage] JPEG TRUNCADO en prefetchCache pág ${pageNum} (${verifyBlob.size} bytes) - DESCARTANDO`);
          prefetchValid = false;
        }
      } catch (verifyErr) {
        console.warn(`[loadPage] Error verificando prefetch pág ${pageNum}: ${verifyErr.message}`);
        prefetchValid = false;
      }
      
      if (!prefetchValid) {
        // Limpiar blob URL corrupto y eliminar del cache
        URL.revokeObjectURL(prefetchedUrl);
        prefetchCache.current.delete(targetCacheKey);
        // NO retornar - continuar con la carga normal por red más abajo
      } else {
        setCurrentImageUrl(prefetchedUrl);
        // Usar dimensiones BASE si están disponibles, sino aproximar
        const baseDims = pageBaseDimensionsRef.current[pageNum];
        if (baseDims) {
          setImageDimensions({ width: baseDims.width, height: baseDims.height });
        } else {
          const pw = typeof prefetched === 'object' ? prefetched.width : 0;
          const ph = typeof prefetched === 'object' ? prefetched.height : 0;
          if (pw > 0 && ph > 0) {
            let w = pw, h = ph;
            if (targetQuality === 'medium' || targetQuality === 'high') {
              w = Math.round(w / 2);
              h = Math.round(h / 2);
            }
            setImageDimensions({ width: w, height: h });
            pageBaseDimensionsRef.current[pageNum] = { width: w, height: h };
            console.log(`[PDF] Dims base aproximadas desde prefetch ${targetQuality}: ${w}x${h}`);
          }
        }
        setCurrentQuality(targetQuality);
        prefetchCache.current.delete(targetCacheKey);
        const prefetchedWidth = typeof prefetched === 'object' ? prefetched.width : 0;
        const prefetchedHeight = typeof prefetched === 'object' ? prefetched.height : 0;
        addToVisitedCache(targetCacheKey, prefetchedUrl, pageNum, prefetchedWidth, prefetchedHeight);
        setPageLoading(false);
        return;
      }
    }
    
    // Buscar también en calidad baja (para mostrar algo rápido)
    const lowCacheKey = `${documentId}_${pageNum}_low`;
    if (visitedCache.current.has(lowCacheKey) && targetQuality !== 'low') {
      const cached = visitedCache.current.get(lowCacheKey);
      setCurrentImageUrl(cached.url);
      // LOW cache siempre tiene las dimensiones correctas
      if (cached.width > 0 && cached.height > 0) {
        setImageDimensions({ width: cached.width, height: cached.height });
        pageBaseDimensionsRef.current[pageNum] = { width: cached.width, height: cached.height };
      }
      setCurrentQuality('low');
      setPageLoading(false);
      return;
    }
    
    setPageLoading(true);
    setLoadingSeconds(0);
    setCurrentQuality('low');
    
    const timer = setInterval(() => {
      setLoadingSeconds(s => s + 1);
    }, 1000);
    
    // Limpiar la imagen actual mientras carga la nueva
    setCurrentImageUrl(null);
    // FIX: No resetear currentImageSize aquí para mantener referencia durante navegación rápida
    
    try {
      const token = localStorage.getItem('token');
      
      // OPTIMIZACIÓN: Cancelar solicitud anterior si existe (navegación rápida)
      if (currentLoadControllerRef.current) {
        currentLoadControllerRef.current.abort();
      }
      
      const controller = new AbortController();
      currentLoadControllerRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), 180000);
      
      // Cargar siempre en calidad baja primero (más rápido)
      // La calidad se mejorará automáticamente después si es necesario
      // CACHE-BUST: Después de 2 reintentos, agregar timestamp para forzar bypass de CDN cache
      const cacheBust = retryCount >= 2 ? `&cb=${Date.now()}` : '';
      // THROTTLE: Usar throttledFetch para evitar saturar el servidor
      const response = await throttledFetch(
        `${API_URL}/api/documentos/manuales/${documentId}/visor/pagina/${pageNum}?quality=${initialQuality}${cacheBust}`,
        { 
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }
      );
      
      clearTimeout(timeoutId);
      clearInterval(timer);
      
      // Manejar código 202 (documento grande en preparación)
      if (response.status === 202) {
        try {
          const data = await response.json();
          const message = data.message || 'Preparando documento grande...';
          const retryAfter = data.retry_after || 5;
          const progress = data.progress || 0;
          
          // MARCAR que estamos esperando preparación - esto desactiva el safety net
          setDocumentPreparing(true);
          
          // Log informativo sin mostrar error
          console.log(`[PDF] ${message} (${progress}%) - Reintentando en ${retryAfter}s...`);
          
          // Si hay progreso, el usuario ve la animación de carga que ya está mostrándose
          // Reintentar automáticamente después del tiempo indicado
          // IMPORTANTE: NO cancelar, esperar pacientemente
          setTimeout(() => {
            if (lastRequestedPageRef.current === pageNum) {
              loadPage(pageNum, 0);
            }
          }, retryAfter * 1000);
          return;
        } catch (jsonErr) {
          console.error('Error parsing 202 response:', jsonErr);
          // Si no es JSON válido, continuar con el flujo normal
        }
      }
      
      // Si llegamos aquí, el documento ya no está en preparación
      setDocumentPreparing(false);
      
      // NUEVO: Manejar error 400 de página fuera de rango
      if (response.status === 400) {
        try {
          const errorData = await response.json();
          if (errorData.error === 'page_out_of_range') {
            console.log(`[PDF] Página ${pageNum} fuera de rango. El documento tiene ${errorData.total_pages} páginas.`);
            // Actualizar el total de páginas y navegar a la última página
            setDocInfo(prev => prev ? { ...prev, total_pages: errorData.total_pages } : prev);
            if (pageNum > errorData.total_pages) {
              setPageNumber(errorData.total_pages);
              loadPage(errorData.total_pages, 0);
            }
            setPageLoading(false);
            setLoadingSeconds(0);
            return;
          }
        } catch (e) {
          // Si no es JSON, continuar con el error normal
        }
      }
      
      // Manejar error 422 - render falló permanentemente (página corrupta/problemática)
      if (response.status === 422) {
        try {
          const errorData = await response.json();
          if (errorData.error === 'render_failed_permanently') {
            console.error(`[PDF] Página ${pageNum} no se puede renderizar: ${errorData.message}`);
            setPageLoading(false);
            setLoadingSeconds(0);
            setError(`No se pudo procesar la página ${pageNum}. Esta página puede estar dañada en el PDF original. Intente limpiar el cache del documento desde el panel de administración y vuelva a intentar.`);
            return;
          }
        } catch (e) {
          // Si no es JSON, continuar con el error normal
        }
      }
      
      // Manejar error 503 - servidor sin memoria suficiente para renderizar
      if (response.status === 503) {
        try {
          const errorData = await response.json();
          if (errorData.error === 'SERVER_LOW_MEMORY') {
            console.warn(`[PDF] Servidor sin memoria para página ${pageNum}: ${errorData.error_detail || ''}`);
            // Reintentar con espera larga (30s), máximo 3 veces
            if (retryCount < 3) {
              const waitTime = 30000; // 30 segundos
              console.log(`[PDF] Reintentando página ${pageNum} en ${waitTime/1000}s (intento ${retryCount + 1}/3) - memoria baja`);
              setError(`El servidor está procesando otros documentos. Reintentando en ${waitTime/1000}s...`);
              setTimeout(() => {
                setError(null);
                loadPage(pageNum, retryCount + 1);
              }, waitTime);
              return;
            }
            setPageLoading(false);
            setLoadingSeconds(0);
            setError('El servidor no tiene suficiente memoria para renderizar esta página. Si el documento es muy grande, intente de nuevo en unos minutos cuando haya menos usuarios conectados.');
            return;
          }
        } catch (e) {
          // Si no es JSON, continuar con el error normal
        }
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        // Detectar si Cloudflare devolvió página de error HTML
        if (errorText.includes('<!DOCTYPE') || errorText.includes('520') || errorText.includes('cloudflare')) {
          throw new Error('SERVER_TEMP_ERROR');
        }
        throw new Error(errorText || 'Error cargando página');
      }
      
      const blob = await response.blob();
      
      // Detectar si el servidor devolvió HTML en lugar de imagen (error de Cloudflare)
      if (blob.type.includes('text/html') || blob.type.includes('text/plain')) {
        const text = await blob.text();
        if (text.includes('<!DOCTYPE') || text.includes('520') || text.includes('cloudflare')) {
          throw new Error('SERVER_TEMP_ERROR');
        }
        throw new Error('El servidor no devolvió una imagen válida');
      }
      
      if (!blob.type.startsWith('image/')) {
        throw new Error('El servidor no devolvió una imagen válida');
      }
      
      // VERIFICACIÓN DE TAMAÑO: Un JPEG válido necesita al menos los marcadores básicos
      // Páginas casi en blanco pueden generar JPEGs pequeños pero válidos (< 3KB)
      if (blob.size < 500) {
        console.warn(`[PDF] Imagen demasiado pequeña (${blob.size} bytes) para página ${pageNum} - no es un JPEG válido`);
        throw new Error('Imagen incompleta - descarga interrumpida');
      }
      
      // VERIFICACIÓN JPEG EOI: Detectar imágenes truncadas (causa páginas "cortadas")
      const jpegComplete = await checkJpegEOI(blob);
      if (!jpegComplete) {
        console.warn(`[PDF] JPEG TRUNCADO para página ${pageNum}: ${blob.size} bytes sin marcador EOI`);
        throw new Error('JPEG_TRUNCATED');
      }
      
      // VERIFICACIÓN DE INTEGRIDAD: Crear imagen para verificar que no está corrupta
      const url = URL.createObjectURL(blob);
      
      // Guardar el tamaño del blob para comparación en upgrade HD
      const loadedBlobSize = blob.size;
      
      // Verificar que la imagen carga correctamente antes de mostrarla
      const img = new Image();
      await new Promise((resolve, reject) => {
        // Timeout para la carga de la imagen local (no debería tardar más de 10 segundos)
        const imgTimeout = setTimeout(() => {
          reject(new Error('Timeout cargando imagen localmente'));
        }, 10000);
        
        img.onload = () => {
          clearTimeout(imgTimeout);
          // Verificar dimensiones mínimas (una imagen corrupta puede tener 0x0)
          if (img.naturalWidth < 50 || img.naturalHeight < 50) {
            console.warn(`[PDF] Imagen con dimensiones sospechosas: ${img.naturalWidth}x${img.naturalHeight}`);
            reject(new Error('Imagen incompleta o corrupta'));
          } else {
            console.log(`[PDF] Página ${pageNum} verificada: ${img.naturalWidth}x${img.naturalHeight}, ${loadedBlobSize} bytes`);
            // GUARDAR DIMENSIONES LOW como base - usadas SIEMPRE para display
            pageBaseDimensionsRef.current[pageNum] = { width: img.naturalWidth, height: img.naturalHeight };
            setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
            resolve();
          }
        };
        img.onerror = (e) => {
          clearTimeout(imgTimeout);
          console.error(`[PDF] Error cargando imagen de página ${pageNum}:`, e);
          reject(new Error('Error cargando imagen - puede estar corrupta'));
        };
        img.src = url;
      });
      
      setCurrentImageUrl(url);
      // FIX CRÍTICO: Guardar tamaño de imagen para comparación en upgrade HD
      setCurrentImageSize(loadedBlobSize);
      setCurrentQuality(initialQuality);
      
      // Guardar en visited cache con límite LRU (incluir dimensiones)
      addToVisitedCache(lowCacheKey, url, pageNum, img.naturalWidth, img.naturalHeight);
      
      setPageLoading(false);
      setLoadingSeconds(0);
      setDocumentPreparing(false); // Limpiar estado de preparación
      
      // Limpiar referencia al controller
      currentLoadControllerRef.current = null;
    } catch (err) {
      clearInterval(timer);
      setDocumentPreparing(false); // Limpiar estado de preparación en error
      
      // Si fue cancelado por navegación rápida, no mostrar error
      // PERO asegurar que si es la página actual, se intente cargar
      if (err.name === 'AbortError') {
        // Verificar si el usuario está en otra página
        if (lastRequestedPageRef.current !== pageNum) {
          console.log(`[PDF] Carga de página ${pageNum} cancelada - usuario navegó a página ${lastRequestedPageRef.current}`);
          return; // Silenciosamente ignorar - el usuario ya está en otra página
        }
        // Si AbortError pero seguimos en la misma página, es timeout o problema de red
        // En este caso, reintentar
        console.log(`[PDF] Solicitud cancelada para página ${pageNum} pero seguimos aquí - reintentando`);
      }
      
      console.error('Error loading page:', err);
      
      // Detectar si es un error de bloqueador de anuncios
      const isBlockedError = err.message.includes('BLOCKED') || 
                             err.message.includes('blocked') ||
                             err.message.includes('Failed to fetch') ||
                             err.message.includes('ERR_BLOCKED');
      
      // Detectar error temporal del servidor (520, Cloudflare)
      const isServerTempError = err.message.includes('SERVER_TEMP_ERROR') ||
                                err.message.includes('520') ||
                                err.message.includes('502') ||
                                err.message.includes('503') ||
                                err.message.includes('504');
      
      // Detectar error de red genérico
      const isNetworkError = err.message.includes('NetworkError') ||
                             err.message.includes('network') ||
                             err.message.includes('Network') ||
                             err.name === 'TypeError';
      
      // Reintentar para errores de red, imagen corrupta, servidor temporal, o bloqueador
      // NO reintentar AbortError de timeout (ya esperamos suficiente)
      const isRetryableError = (err.name === 'AbortError' && lastRequestedPageRef.current === pageNum) || 
                               err.message.includes('fetch') || 
                               err.message.includes('corrupta') ||
                               err.message.includes('incompleta') ||
                               err.message.includes('JPEG_TRUNCATED') ||
                               err.message.includes('Timeout cargando imagen localmente') ||
                               isServerTempError ||
                               isNetworkError ||
                               isBlockedError;
      
      // Reintentar con más tolerancia para PDFs grandes que necesitan descargarse
      if (retryCount < 10 && isRetryableError) {
        const waitTime = 2000 + (retryCount * 2000); // Espera incremental más larga
        console.log(`Reintentando página ${pageNum} (intento ${retryCount + 1}/11) - ${err.message} - esperando ${waitTime/1000}s`);
        setTimeout(() => loadPage(pageNum, retryCount + 1), waitTime);
        return;
      }
      
      setPageLoading(false);
      setLoadingSeconds(0);
      
      if (err.name === 'AbortError') {
        setError('La carga del documento tomó demasiado tiempo. Por favor intente de nuevo.');
      } else if (err.message.includes('corrupta') || err.message.includes('incompleta')) {
        setError('La página no se cargó correctamente. Por favor intente de nuevo con el botón de recargar.');
      } else if (isServerTempError) {
        setError('El servidor tuvo un problema temporal. Por favor espere unos segundos y presione el botón de recargar.');
      } else if (isNetworkError) {
        setError('Error de conexión. Por favor verifique su internet e intente de nuevo.');
      } else if (isBlockedError) {
        setError('Error de carga. Si usa bloqueador de anuncios, intente desactivarlo temporalmente para este sitio.');
      } else {
        setError(`Error cargando página: ${err.message}`);
      }
    }
  };

  const goToPage = (num) => {
    if (num >= 1 && num <= (docInfo?.total_pages || 1)) {
      currentPageRef.current = num; // SYNC: actualizar ANTES del state para cerrar race condition
      setPageNumber(num);
      setError(null);
      setRotation(0); // Resetear rotación al cambiar de página
    }
  };

  // Hook aislado: registra apertura, muestra toast "Continuar" y guarda página al cerrar.
  useReadingProgress({
    isOpen,
    documentId,
    currentPage: pageNumber,
    onJumpToPage: goToPage,
  });

  // Hook aislado: marcadores (bookmarks) del documento — propios + compartidos del taller
  const {
    marcadores,
    loading: marcadoresLoading,
    crear: crearMarcador,
    eliminar: eliminarMarcador,
  } = useMarcadores(documentId, isOpen);

  // Hook aislado: stats del documento (revisión + usuarios únicos) — alimenta la
  // marquesina rotativa en la toolbar. Si falla, la toolbar cae al modo "Ref:" tradicional.
  const documentStats = useDocumentStats(documentId, isOpen);

  // Medición dinámica de la altura del toolbar para anclar los paneles laterales
  // (Índice y Marcadores) exactamente al ras. Funciona en cualquier DPI/zoom/cantidad de filas.
  useLayoutEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setToolbarHeight(Math.round(h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isFullscreen, loading]);

  // Estado UI para panel de marcadores
  const [showBookmarksPanel, setShowBookmarksPanel] = useState(false);

  // Helper unificado para navegar a una página desde la UI (toolbar input, flechas footer, etc.).
  //  - Modo página individual: usa goToPage tradicional (motor del visor).
  //  - Modo continuo: scrollIntoView a [data-page-num="N"] (mismo patrón seguro que PDFTableOfContents).
  //    NO toca el goToPage del motor para evitar conflicto con el IntersectionObserver de PDFContinuousView.
  const navegarAPagina = useCallback((pg) => {
    const n = parseInt(pg, 10);
    const total = docInfo?.total_pages || 0;
    if (!n || n < 1 || (total > 0 && n > total)) return;
    if (continuousMode && containerRef.current) {
      const targetEl = containerRef.current.querySelector(`[data-page-num="${n}"]`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    goToPage(n);
  }, [continuousMode, docInfo, goToPage]);

  // Auto-abrir el panel cuando el documento tiene marcadores (una sola vez por sesión).
  // Si el usuario lo cierra, no lo vuelve a abrir hasta cambiar de documento.
  // Solo en escritorio (≥1024px) — en mobile el panel ocupa toda la pantalla.
  const bookmarksAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      bookmarksAutoOpenedRef.current = false;
      return;
    }
    if (bookmarksAutoOpenedRef.current) return;
    // No abrir automáticamente en mobile/tablet
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      bookmarksAutoOpenedRef.current = true;
      return;
    }
    if (marcadores && marcadores.length > 0) {
      setShowBookmarksPanel(true);
      bookmarksAutoOpenedRef.current = true;
    }
  }, [isOpen, marcadores]);

  // NOTA HISTÓRICA (15-feb-2026): Se intentó 2 veces implementar "abrir visor directo
  // en la página marcada" desde el link de la campanita. Conflictó con el toast
  // "Continuar leyendo" de useReadingProgress (compiten por scrollear a páginas distintas).
  // Decisión del usuario: dejar que la notif abra el documento y el panel de marcadores
  // — el usuario hace 1 click extra en el marcador del panel para ir a la página.
  // NO REIMPLEMENTAR sin antes resolver el conflicto con useReadingProgress.

  // Saltar a la página de un marcador (funciona en single-page y continuo)
  const jumpToBookmark = useCallback((targetPage) => {
    goToPage(targetPage);
    setTimeout(() => {
      const el = document.querySelector(`[data-page-num="${targetPage}"]`);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, 150);
  }, [goToPage]);

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3.0));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  const handleZoomReset = () => setScale(1.0);
  
  // Rotación de página
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const isRotatedSideways = rotation === 90 || rotation === 270;

  // Limpiar cache HD del documento actual (solo super admin)
  const [clearingCache, setClearingCache] = useState(false);
  const handleClearHDCache = async () => {
    if (!isSuperAdmin || clearingCache) return;
    if (!window.confirm('¿Purgar el cache de ESTE documento (disco local + Cloudflare R2) y re-renderizar?\n\nSe usa cuando el visor muestra páginas viejas o mezcladas tras una resubida. NO borra el PDF: solo las imágenes cacheadas, que se vuelven a generar limpias al navegar.\n\nNo envía notificaciones ni cambia la revisión.')) return;
    setClearingCache(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/documentos/manuales/${documentId}/cache-paginas`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      // Limpiar TODOS los caches del frontend
      visitedCache.current.clear();
      hdRetryCountsRef.current = {};
      // Forzar recarga completa del documento desde la página actual
      setCurrentImageUrl(null);
      setCurrentQuality('low');
      setImageKey(prev => prev + 1);
      loadPage(pageNumber, 0);
      alert(`Cache purgado: ${data.disk_cleared || 0} de disco, ${data.r2_cleared || 0} de Cloudflare.\nLas páginas se re-renderizarán limpias al navegar.`);
    } catch (e) {
      alert('Error purgando cache. El servidor puede estar ocupado.\nIntentá de nuevo en unos segundos.');
    } finally {
      setClearingCache(false);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  // Función de búsqueda de texto
  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchError('Ingresa al menos 2 caracteres');
      return;
    }
    
    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/documentos/manuales/${documentId}/visor/buscar?q=${encodeURIComponent(searchQuery)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        throw new Error('Error en la búsqueda');
      }
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        // Agrupar resultados por página
        const groupedResults = data.results.reduce((acc, result) => {
          const pageNum = result.page_num;
          if (!acc[pageNum]) {
            acc[pageNum] = { page_num: pageNum, matches: [] };
          }
          acc[pageNum].matches.push(result.contexto);
          return acc;
        }, {});
        
        setSearchResults(Object.values(groupedResults).sort((a, b) => a.page_num - b.page_num));
      } else {
        setSearchResults([]);
        setSearchError('No se encontraron resultados');
      }
    } catch (err) {
      console.error('Search error:', err);
      setSearchError('Error al buscar. Intenta de nuevo.');
    } finally {
      setIsSearching(false);
    }
  };

  const goToSearchResult = (pageNum) => {
    goToPage(pageNum);
    setShowSearchPanel(false);
  };

  // Impresión controlada - Abre diálogo de impresión del navegador en modo "Imprimir"
  const handlePrint = async () => {
    if (!canPrint) return;
    
    const pages = printPages.trim() || String(pageNumber);
    setIsPrinting(true);
    setPrintError(null);
    setPrintProgress(0);
    
    try {
      // Parsear páginas solicitadas
      const pageList = [];
      const parts = pages.split(',');
      for (const part of parts) {
        if (part.includes('-')) {
          const [start, end] = part.split('-').map(n => parseInt(n.trim()));
          for (let i = start; i <= Math.min(end, docInfo.total_pages); i++) {
            if (i >= 1) pageList.push(i);
          }
        } else {
          const num = parseInt(part.trim());
          if (num >= 1 && num <= docInfo.total_pages) pageList.push(num);
        }
      }
      
      // Eliminar duplicados, ordenar y limitar
      const pagesToPrint = [...new Set(pageList)].sort((a,b) => a-b).slice(0, maxPrintPages);
      
      if (pagesToPrint.length === 0) {
        setPrintError('No hay páginas válidas para imprimir');
        setIsPrinting(false);
        return;
      }
      
      // Crear ventana de impresión
      const printWindow = window.open('', '_blank', 'width=900,height=700');
      if (!printWindow) {
        setPrintError('Por favor, permite las ventanas emergentes para imprimir');
        setIsPrinting(false);
        return;
      }
      
      // Obtener info del taller para marca de agua — SIEMPRE refrescar desde /auth/me
      // (localStorage puede quedar stale tras una sesión larga o cambio de datos en el admin).
      let userInfo = JSON.parse(localStorage.getItem('user') || '{}');
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        const token = localStorage.getItem('token');
        if (token) {
          const meRes = await fetch(`${apiBase}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (meRes.ok) {
            const fresh = await meRes.json();
            userInfo = fresh;
            localStorage.setItem('user', JSON.stringify(fresh));
          }
        }
      } catch (e) {
        // silencioso — seguimos con lo que haya en localStorage
      }

      const tallerData = userInfo.taller_info || userInfo.taller || {};
      const tallerNombre = tallerData.razon_social || userInfo.full_name || userInfo.nombre || 'Usuario';
      const numeroMatricula = tallerData.numero_anac || '—';
      const userEmail = userInfo.email || '';
      const paisTaller = tallerData?.ubicacion?.pais || 'Argentina';

      // Resolver sigla nacional dinámicamente (ANAC / DINACIA / DGAC...)
      // Fallback estático en caso de fallo de red — coincide con el seed de /paises/
      const FALLBACK_SIGLAS = {
        'Argentina': 'ANAC',
        'Brasil': 'ANAC',
        'Uruguay': 'DINACIA',
        'Bolivia': 'DGAC',
      };
      let siglaAutoridad = FALLBACK_SIGLAS[paisTaller] || 'ANAC';
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${apiBase}/api/paises/publicos`);
        if (res.ok) {
          const data = await res.json();
          const found = (data.items || []).find(p => p.nombre === paisTaller);
          if (found?.sigla_autoridad) siglaAutoridad = found.sigla_autoridad;
        }
      } catch (e) {
        // silencioso — usamos el fallback estático
      }

      const fechaHora = new Date().toLocaleString('es-AR');

      // Escapar valores de usuario para prevenir XSS
      const escapeHtml = (str) => String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
      const safeDocName = escapeHtml(documentName);
      const safeTallerNombre = escapeHtml(tallerNombre);
      const safeSigla = escapeHtml(siglaAutoridad);
      const safeNumero = escapeHtml(numeroMatricula);
      const safeEmail = escapeHtml(userEmail);
      
      // HTML base con estilos para impresión - INCLUYE MARCA DE AGUA CSS
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${safeDocName} - Impresión</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              background: #f5f5f5; 
              font-family: 'Segoe UI', Arial, sans-serif;
              padding: 20px;
            }
            .loading-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 80vh;
              text-align: center;
            }
            .loading-spinner {
              width: 60px;
              height: 60px;
              border: 4px solid #e0e0e0;
              border-top: 4px solid #0d9488;
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin-bottom: 20px;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .progress-bar {
              width: 300px;
              height: 8px;
              background: #e0e0e0;
              border-radius: 4px;
              overflow: hidden;
              margin: 15px 0;
            }
            .progress-fill {
              height: 100%;
              background: linear-gradient(90deg, #0d9488, #0ea5e9);
              transition: width 0.3s;
            }
            .page { 
              position: relative;
              page-break-after: always; 
              text-align: center; 
              padding: 0;
              background: white;
              margin-bottom: 20px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              overflow: hidden;
            }
            .page:last-child { page-break-after: avoid; margin-bottom: 0; }
            .page img { max-width: 100%; height: auto; display: block; margin: 0 auto; }
            
            /* Marca de agua diagonal profesional - teal C&M conservador,
               funciona bien tanto en color como en B&N (se convierte a gris medio) */
            .watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-35deg);
              font-size: 22px;
              color: rgba(13, 148, 136, 0.22);
              white-space: nowrap;
              pointer-events: none;
              z-index: 10;
              font-weight: 700;
              letter-spacing: 4px;
              text-transform: uppercase;
              font-family: 'Arial', sans-serif;
            }
            
            /* Segunda línea de marca de agua para mayor cobertura */
            .watermark-secondary {
              position: absolute;
              top: 25%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-35deg);
              font-size: 16px;
              color: rgba(13, 148, 136, 0.16);
              white-space: nowrap;
              pointer-events: none;
              z-index: 10;
              font-weight: 500;
              letter-spacing: 3px;
              font-family: 'Arial', sans-serif;
            }
            
            .watermark-tertiary {
              position: absolute;
              top: 75%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-35deg);
              font-size: 16px;
              color: rgba(13, 148, 136, 0.16);
              white-space: nowrap;
              pointer-events: none;
              z-index: 10;
              font-weight: 500;
              letter-spacing: 3px;
              font-family: 'Arial', sans-serif;
            }
            
            /* Header profesional - barra teal sólida con sigla nacional dinámica */
            .page-header {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              padding: 5px 14px;
              font-size: 9px;
              color: #ffffff;
              background: rgba(13, 148, 136, 0.92);
              text-align: left;
              z-index: 11;
              font-family: 'Arial', sans-serif;
              letter-spacing: 0.8px;
              font-weight: 700;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .page-header span { opacity: 1; }
            
            /* Footer profesional - info de trazabilidad VISIBLE */
            .page-footer {
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              padding: 5px 14px;
              font-size: 8.5px;
              color: #0f766e;
              background: rgba(13, 148, 136, 0.08);
              border-top: 1px solid rgba(13, 148, 136, 0.5);
              text-align: center;
              z-index: 11;
              font-family: 'Arial', sans-serif;
              letter-spacing: 0.3px;
              font-weight: 600;
            }
            
            /* Configuración de página: SIN URL ni timestamp del navegador */
            @page {
              size: A4;
              margin: 0;
            }
            
            @media print { 
              html, body { background: white; padding: 0; margin: 0; }
              .page { 
                page-break-after: always; 
                box-shadow: none;
                margin: 0;
                padding: 0;
              }
              /* En impresión mantenemos opacidad para que se vea también en B&N */
              .watermark { color: rgba(13, 148, 136, 0.28); }
              .watermark-secondary, .watermark-tertiary { color: rgba(13, 148, 136, 0.20); }
              .page-header { 
                background: rgba(13, 148, 136, 0.92) !important;
                color: #ffffff !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .page-footer { 
                background: rgba(13, 148, 136, 0.10) !important;
                color: #0f766e !important;
                border-top: 1px solid rgba(13, 148, 136, 0.5) !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="loading-container" id="loading">
            <div class="loading-spinner"></div>
            <h2 style="color: #333; margin-bottom: 10px;">Preparando impresión...</h2>
            <p style="color: #666;">Cargando <span id="current">0</span> de ${pagesToPrint.length} páginas</p>
            <div class="progress-bar">
              <div class="progress-fill" id="progress" style="width: 0%"></div>
            </div>
          </div>
          <div id="pages" style="display: none;"></div>
        </body>
        </html>
      `);
      
      const token = localStorage.getItem('token');
      const pagesContainer = printWindow.document.getElementById('pages');
      const loadingContainer = printWindow.document.getElementById('loading');
      const currentSpan = printWindow.document.getElementById('current');
      const progressBar = printWindow.document.getElementById('progress');
      
      // Cargar cada página
      for (let i = 0; i < pagesToPrint.length; i++) {
        const pageNum = pagesToPrint[i];
        setPrintProgress(Math.round((i / pagesToPrint.length) * 100));
        
        if (currentSpan) currentSpan.textContent = i + 1;
        if (progressBar) progressBar.style.width = `${((i + 1) / pagesToPrint.length) * 100}%`;
        
        const pageDiv = printWindow.document.createElement('div');
        pageDiv.className = 'page';
        
        try {
          // Función para cargar una página con reintentos
          const loadPageWithRetry = async (pageNum, maxRetries = 30) => {
            for (let attempt = 0; attempt < maxRetries; attempt++) {
              const response = await fetch(
                `${API_URL}/api/documentos/manuales/${documentId}/visor/pagina-imprimible/${pageNum}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
              );
              
              // Si el documento está preparándose (202), esperar y reintentar
              if (response.status === 202) {
                try {
                  const data = await response.json();
                  const message = data.message || 'Preparando documento...';
                  const retryAfter = data.retry_after || 3;
                  
                  // Actualizar mensaje en la ventana de impresión
                  const loadingText = printWindow.document.querySelector('.loading-container h2');
                  if (loadingText) {
                    loadingText.textContent = message;
                  }
                  
                  console.log(`[Print] Página ${pageNum}: ${message} (intento ${attempt + 1})`);
                  
                  // Esperar antes de reintentar
                  await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
                  continue;
                } catch (e) {
                  console.error('Error parsing 202:', e);
                }
              }
              
              return response;
            }
            return null;
          };
          
          const response = await loadPageWithRetry(pageNum);
          
          if (response && response.ok) {
            const blob = await response.blob();
            const img = printWindow.document.createElement('img');
            img.src = URL.createObjectURL(blob);
            img.alt = `Página ${pageNum}`;
            pageDiv.appendChild(img);
            
            // === MARCA DE AGUA PROFESIONAL ===

            // Marca de agua principal (centro): COPIA AUTORIZADA + nombre del taller
            const watermark = printWindow.document.createElement('div');
            watermark.className = 'watermark';
            watermark.textContent = `COPIA AUTORIZADA · ${safeTallerNombre.toUpperCase()}`;
            pageDiv.appendChild(watermark);

            // Marca de agua secundaria (arriba): sigla nacional + matrícula
            const watermark2 = printWindow.document.createElement('div');
            watermark2.className = 'watermark-secondary';
            watermark2.textContent = `${safeSigla} ${safeNumero} · DOCUMENTO CONTROLADO`;
            pageDiv.appendChild(watermark2);

            // Marca de agua terciaria (abajo): igual a la secundaria
            const watermark3 = printWindow.document.createElement('div');
            watermark3.className = 'watermark-tertiary';
            watermark3.textContent = `${safeSigla} ${safeNumero} · DOCUMENTO CONTROLADO`;
            pageDiv.appendChild(watermark3);

            // Header barra teal: sigla nacional dinámica + matrícula + nombre del taller (sin "C&M")
            const header = printWindow.document.createElement('div');
            header.className = 'page-header';
            const headerLeft = printWindow.document.createElement('span');
            headerLeft.textContent = `DOCUMENTO CONTROLADO · ${safeSigla} ${safeNumero}`;
            const headerRight = printWindow.document.createElement('span');
            headerRight.textContent = safeTallerNombre;
            header.appendChild(headerLeft);
            header.appendChild(headerRight);
            pageDiv.appendChild(header);

            // Footer: usuario que imprime, fecha y página — VISIBLE, sin URL
            const footer = printWindow.document.createElement('div');
            footer.className = 'page-footer';
            const userPart = safeEmail ? `Impreso por ${safeEmail} · ` : '';
            footer.textContent = `${userPart}Fecha: ${fechaHora} · Pág. ${pageNum}`;
            pageDiv.appendChild(footer);
          } else {
            const errorMsg = printWindow.document.createElement('p');
            errorMsg.style.cssText = 'padding: 50px; color: #999;';
            errorMsg.textContent = `Error cargando página ${pageNum}`;
            pageDiv.appendChild(errorMsg);
          }
        } catch (e) {
          console.error(`Error loading page ${pageNum}:`, e);
          const errorMsg = printWindow.document.createElement('p');
          errorMsg.style.cssText = 'padding: 50px; color: #999;';
          errorMsg.textContent = `Error cargando página ${pageNum}`;
          pageDiv.appendChild(errorMsg);
        }
        
        pagesContainer.appendChild(pageDiv);
      }
      
      setPrintProgress(100);
      
      // Mostrar páginas y ocultar loading
      if (loadingContainer) loadingContainer.style.display = 'none';
      if (pagesContainer) pagesContainer.style.display = 'block';
      
      // Abrir diálogo de impresión (no descarga)
      setTimeout(() => {
        printWindow.focus();
        // Usar execCommand para forzar el diálogo de impresión nativo
        printWindow.print();
        setIsPrinting(false);
        setShowPrintDialog(false);
        setPrintProgress(0);
      }, 800);
      
    } catch (err) {
      console.error('Print error:', err);
      setPrintError(err.message || 'Error al preparar la impresión');
      setIsPrinting(false);
      setPrintProgress(0);
    }
  };

  // Descarga directa SOLO para super admins
  const handleDownload = async () => {
    if (!isSuperAdmin) return;
    
    const token = localStorage.getItem('token');
    const url = `${API_URL}/api/documentos/manuales/${documentId}/pdf-file`;
    
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${documentName}.pdf`;
        a.click();
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  if (!isOpen) return null;

  const displayName = documentName || 'Documento PDF';
  const numPages = docInfo?.total_pages || 0;

  // Callback compartido para crear marcador desde el FAB flotante
  const handleAddBookmark = async ({ nota, compartido }) => {
    const res = await crearMarcador({ pagina: pageNumber, nota, compartido });
    if (res) setShowBookmarksPanel(true);
    return res;
  };

  // Set de páginas que tienen marcador (para la cintita en modo continuo)
  const bookmarkedPages = new Set(marcadores.map((m) => m.pagina));

  // Props compartidas entre sub-componentes
  const toolbarProps = {
    displayName, pageNumber, numPages, numeroReferencia,
    goToPage, scale, isUpgradingQuality,
    handleZoomIn, handleZoomOut, handleZoomReset,
    toggleFullscreen,
    showSearchPanel, setShowSearchPanel,
    showTableOfContents, setShowTableOfContents,
    showThumbnails, setShowThumbnails,
    showKeyboardHelp, setShowKeyboardHelp,
    nightMode, setNightMode,
    rotation, handleRotate,
    canPrint, setShowPrintDialog,
    isSuperAdmin, isAdmin, handleDownload,
    clearingCache, handleClearHDCache,
    documentId,
    // Estado de renderizado del PDF actual (viene de /visor/info):
    // si docInfo.r2_file_key === docInfo.precache_done_r2_key → ya está renderizado ✓
    alreadyRendered: !!(docInfo?.r2_file_key && docInfo?.precache_done_r2_key && docInfo.r2_file_key === docInfo.precache_done_r2_key),
    pdfSizeBytes: docInfo?.pdf_size || 0,
    cachedPages, tableOfContents, tocLoading,
    onClose,
    continuousMode, setContinuousMode,
    // Marcadores
    showBookmarksPanel, setShowBookmarksPanel,
    marcadoresCount: marcadores.length,
    // Marquesina rotativa (revisión + usuarios únicos)
    documentStats,
    // Ref del container para scrollear en modo continuo desde el input/arrows de la toolbar
    containerRef,
    // Helper unificado de navegación (mismo que usa el footer mobile)
    navegarAPagina,
    // Ref para medición dinámica de altura (anclado de paneles)
    innerRef: toolbarRef,
  };

  const footerProps = {
    pageNumber, numPages, goToPage, navegarAPagina,
    scale, handleZoomIn, handleZoomOut,
    isSuperAdmin, isAdmin, canPrint, maxPrintPages
  };

  const pageDisplayProps = {
    error, setError, loadPage, loadDocumentInfo,
    pageLoading, pageNumber, currentImageUrl,
    imageDimensions, scale, rotation, nightMode,
    imageKey, currentQuality, isSuperAdmin,
    loadingSeconds, currentQuote
  };

  // Contenido del area principal (compartido entre fullscreen y dialog)
  const renderContentArea = (fsMode) => (
    <div 
      ref={containerRef}
      className="flex-1 min-h-0 overflow-auto bg-slate-900 flex items-start justify-center p-4 md:p-8 relative"
      onContextMenu={(e) => !isSuperAdmin && e.preventDefault()}
    >
      <PDFSearchPanel
        showSearchPanel={showSearchPanel} setShowSearchPanel={setShowSearchPanel}
        searchQuery={searchQuery} setSearchQuery={setSearchQuery}
        searchResults={searchResults} isSearching={isSearching} searchError={searchError}
        handleSearch={handleSearch} goToSearchResult={goToSearchResult}
        continuousMode={continuousMode} containerRef={containerRef}
      />

      {!continuousMode && fsMode && (
        <PDFPageList
          showPageList={showTableOfContents && !tableOfContents.length}
          setShowPageList={setShowTableOfContents}
          numPages={numPages} pageNumber={pageNumber} goToPage={goToPage} cachedPages={cachedPages}
        />
      )}

      {/* Miniaturas: ahora también renderizan en modo continuo (click → scroll suave a la página).
          En modo individual usan goToPage tradicional. */}
      <PDFThumbnails
        showThumbnails={showThumbnails} setShowThumbnails={setShowThumbnails}
        numPages={numPages} pageNumber={pageNumber} goToPage={goToPage} documentId={documentId}
        continuousMode={continuousMode} containerRef={containerRef}
        toolbarHeight={toolbarHeight}
      />

      <PDFTableOfContents
        showTableOfContents={showTableOfContents && tableOfContents.length > 0}
        setShowTableOfContents={setShowTableOfContents}
        tableOfContents={tableOfContents} tocLoading={tocLoading}
        pageNumber={pageNumber} goToPage={goToPage} docInfo={docInfo}
        continuousMode={continuousMode} containerRef={containerRef}
        toolbarHeight={toolbarHeight}
      />

      <PDFKeyboardHelp showKeyboardHelp={showKeyboardHelp} setShowKeyboardHelp={setShowKeyboardHelp} />

      {/* Panel lateral de marcadores (over the page) */}
      <PDFBookmarksPanel
        show={showBookmarksPanel}
        onClose={() => setShowBookmarksPanel(false)}
        marcadores={marcadores}
        loading={marcadoresLoading}
        onJumpToPage={(p) => { jumpToBookmark(p); setShowBookmarksPanel(false); }}
        onDelete={eliminarMarcador}
        toolbarHeight={toolbarHeight}
      />

      {continuousMode ? (
        <PDFContinuousView
          documentId={documentId}
          numPages={numPages}
          pageNumber={pageNumber}
          goToPage={goToPage}
          scale={scale}
          nightMode={nightMode}
          rotation={rotation}
          isSuperAdmin={isSuperAdmin}
          containerRef={containerRef}
          bookmarkedPages={bookmarkedPages}
        />
      ) : (
        <PDFPageDisplay {...pageDisplayProps} />
      )}

      {/* FAB flotante para agregar marcador — visible en ambos modos */}
      <PDFBookmarkPopover
        pagina={pageNumber}
        userTienetaller={!!userInfo?.taller_id}
        onConfirm={handleAddBookmark}
        side="top"
        align="end"
      >
        <button
          type="button"
          className="fixed bottom-20 right-6 z-[60] h-14 w-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-2xl shadow-amber-500/40 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center group"
          title="Agregar marcador en esta página"
          data-testid="fab-add-bookmark-btn"
          aria-label="Agregar marcador"
        >
          <BookmarkPlus className="h-6 w-6" />
          <span className="absolute right-full mr-3 px-2 py-1 rounded-md bg-slate-900 text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-slate-700">
            + Marcador pág. {pageNumber}
          </span>
        </button>
      </PDFBookmarkPopover>
    </div>
  );

  // MODO FULLSCREEN - renderizado como portal independiente
  // Renderizamos también durante el loading para evitar el "flash" inicial donde
  // el viewer aparece en la esquina superior izquierda por la animación de Dialog.
  if (isFullscreen) {
    return (
      <>
        <div 
          className="fixed inset-0 z-[9999] flex flex-col bg-slate-900"
          data-testid="pdf-viewer-pro-fullscreen"
        >
          {loading && (
            <>
              <PDFLoadingScreen
                loadingStage={loadingStage}
                loadingProgress={loadingProgress}
                currentQuote={currentQuote}
                displayName={displayName}
              />
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar visor"
                data-testid="pdf-viewer-close-loading"
                className="absolute top-4 right-4 z-[60] h-10 w-10 flex items-center justify-center rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-200 hover:text-white transition-colors border border-slate-700"
              >
                <span className="text-xl leading-none">×</span>
              </button>
            </>
          )}
          {!loading && <PDFToolbar {...toolbarProps} isFullscreen={true} />}
          {!loading && renderContentArea(true)}
          {!loading && <PDFFooter {...footerProps} />}
        </div>

        <PDFPrintDialog
          showPrintDialog={showPrintDialog} setShowPrintDialog={setShowPrintDialog}
          displayName={displayName} numPages={numPages} pageNumber={pageNumber} maxPrintPages={maxPrintPages}
          printPages={printPages} setPrintPages={setPrintPages}
          isPrinting={isPrinting} printProgress={printProgress} printError={printError}
          handlePrint={handlePrint}
        />

        <style>{`
          @keyframes loading-bar {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(400%); }
          }
          .animate-loading-bar {
            animation: loading-bar 1.5s ease-in-out infinite;
          }
          /* Ocultar las flechitas spinner nativas del input numérico de página
             (redundantes con los botones < > a sus costados). */
          .pdf-page-input::-webkit-outer-spin-button,
          .pdf-page-input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .pdf-page-input { -moz-appearance: textfield; appearance: textfield; }
        `}</style>
      </>
    );
  }

  // MODO NORMAL - dentro del Dialog
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent 
          className={`${isFullscreen ? 'fixed inset-0 w-screen h-screen max-w-none rounded-none z-[9999]' : 'max-w-7xl h-[95vh]'} flex flex-col p-0 overflow-hidden bg-slate-900`}
          data-testid="pdf-viewer-pro"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{displayName}</DialogTitle>
            <DialogDescription>Visualizador Premium de Documentos Tecnicos Aeronauticos</DialogDescription>
          </DialogHeader>
          
          {/* Pantalla de carga */}
          {loading && (
            <PDFLoadingScreen
              loadingStage={loadingStage}
              loadingProgress={loadingProgress}
              currentQuote={currentQuote}
              displayName={displayName}
            />
          )}

          {/* Header */}
          {!loading && <PDFToolbar {...toolbarProps} isFullscreen={false} />}

          {/* Area de contenido */}
          {!loading && renderContentArea(false)}

          {/* Footer */}
          {!loading && <PDFFooter {...footerProps} />}
        </DialogContent>
      </Dialog>

      <PDFPrintDialog
        showPrintDialog={showPrintDialog} setShowPrintDialog={setShowPrintDialog}
        displayName={displayName} numPages={numPages} pageNumber={pageNumber} maxPrintPages={maxPrintPages}
        printPages={printPages} setPrintPages={setPrintPages}
        isPrinting={isPrinting} printProgress={printProgress} printError={printError}
        handlePrint={handlePrint}
      />

      <style>{`
        @keyframes loading-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        .animate-loading-bar {
          animation: loading-bar 1.5s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default PDFViewerPro;

